import { FormField, Inline, InputGroup, Paragraph, Select } from '@george-labs.com/design-system';
import type {
    ItemPropTypes,
    SectionPropTypes,
} from '@george-labs.com/design-system/types/components/Dropdown';
import type { FORM_FIELD_LABEL_POSITION } from '@george-labs.com/design-system/types/components/Form';
import type { ColSize } from '@george-labs.com/design-system/types/components/Layout';
import type { PropTypes as SelectPropTypes } from '@george-labs.com/design-system/types/components/Select/SelectPropTypes';
import type { ComponentProps, ReactNode } from 'react';
import { useMemo } from 'react';

import { useField } from '@luna/form2';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

export type FormSelectFieldItem = { 'data-cy'?: string } & ItemPropTypes;

interface Props<FormValues>
    extends FormFieldProps<FormValues>,
        Pick<SelectPropTypes<FormValues>, 'renderSelection'> {
    items: FormSelectFieldItem[] | SectionPropTypes<FormSelectFieldItem>[];
    labelColumnWidth?: ColSize;
    isLoading?: boolean;
    multiple?: boolean;
    required?: boolean;
    disabled?: boolean;
    selectedItemsText?: string;
    showSelectedCount?: boolean;
    allowSelectAll?: boolean;
    clearItemLabel?: string;
    inputProps?: Partial<ComponentProps<typeof Select>>;
    className?: string;
    menuClassName?: string;
    isErrorVisible?: boolean;
    append?: ReactNode;
    prepend?: ReactNode;
    error?: string;
    shouldClearOnUnmount?: boolean;
}

interface FormSelectFieldStatics {
    LABEL_POSITION: typeof FORM_FIELD_LABEL_POSITION;
}

const LABEL_COLUMN_WIDTH = 4;

export function FormSelectField<FormValues = void>({
    'data-cy': dataCy,
    fieldName,
    placeholder,
    items,
    label,
    labelPosition,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    isLoading = false,
    multiple = false,
    required = false,
    disabled = false,
    description,
    selectedItemsText,
    allowSelectAll,
    showSelectedCount = false,
    clearItemLabel,
    renderSelection,
    inputProps,
    className,
    menuClassName,
    isErrorVisible = true,
    warning,
    info,
    alert,
    addon,
    append,
    prepend,
    error: errorOverride,
    shouldClearOnUnmount,
    showErrorBeforeBlur,
    compact,
}: Props<FormValues>) {
    const [
        { value: fieldValue, onBlur },
        { error: validationError, touched, dirty },
        { setTouched, setValue },
    ] = useField<string | string[]>(fieldName, { shouldClearOnUnmount });

    const isErrorMessageVisible = shouldShowError(
        isErrorVisible,
        touched,
        validationError,
        showErrorBeforeBlur,
        dirty,
    );

    const handleSelect = (selected: ItemPropTypes | ItemPropTypes[] | null) => {
        let value = null;

        if (selected && !Array.isArray(selected)) {
            value = selected.value;
        } else if (Array.isArray(selected) && selected.length) {
            value = selected.map(({ value: selectedItemValue }) => selectedItemValue);
        }

        setValue(value);
        setTouched();
        inputProps?.onSelect?.(selected);
    };

    const value = useMemo(() => {
        if (Array.isArray(fieldValue)) {
            return fieldValue.map((item) => {
                return {
                    value: item,
                };
            });
        }

        return fieldValue;
    }, [fieldValue]);

    return (
        <FormField
            contentClassName="m-0"
            description={description}
            label={label && <Inline data-cy={`${dataCy}-label`}>{label}</Inline>}
            labelPosition={labelPosition}
            labelColumnWidth={labelColumnWidth}
            required={required}
            disabled={disabled}
            error={
                (!!errorOverride || isErrorMessageVisible) && (
                    <Paragraph data-cy={`${dataCy}-error`}>
                        {errorOverride || validationError}
                    </Paragraph>
                )
            }
            className={className}
            input={
                <InputGroup disabled={disabled} prepend={prepend} append={append}>
                    <Select
                        placeholder={placeholder}
                        {...inputProps}
                        allowSelectAll={allowSelectAll}
                        showSelectedCount={showSelectedCount}
                        selectedItemsText={selectedItemsText}
                        name={fieldName}
                        value={value}
                        items={items}
                        isLoading={isLoading}
                        multiple={multiple}
                        onSelect={handleSelect}
                        onBeforeClose={onBlur}
                        data-cy={dataCy}
                        menuClassName={menuClassName}
                        clearItemLabel={clearItemLabel}
                        renderSelection={renderSelection}
                        // TODO Remove when GDSWEB-5691 is done
                        translate="no"
                    />
                </InputGroup>
            }
            warning={warning}
            info={info}
            alert={alert}
            addon={addon}
            compact={compact}
        />
    );
}

export type FormSelectField = typeof FormSelectField & FormSelectFieldStatics;

FormSelectField.LABEL_POSITION = FormField.LABEL_POSITION;
